import { useTranslations } from "providers";
import React, { PropsWithChildren, useCallback, useMemo, useState } from "react";
import { useCookies } from "react-cookie";
import { View } from "views/View";

import { AgendaItemViewsContext, AgendaItemViewsContextProps } from "./AgendaItemViewsContext";

const CookieKey = "AgendaItems:SelectedViewName";

export const AgendaItemViewsProvider: React.FC<PropsWithChildren<unknown>> = ({ children }) => {
  const i18n = useTranslations();
  const [cookies, setCookie] = useCookies([CookieKey], { doNotParse: true, doNotUpdate: true });
  const [selectedViewName, setSelectedViewName] = useState(cookies[CookieKey] || undefined);

  const views = useMemo<View[]>(
    () => [
      {
        name: "Overview",
        title: i18n.View_AgendaItems_Overview,
        type: "Grid",
        icon: <i className="ms-Icon ms-Icon--SingleColumn" />,
      },
      {
        name: "Details",
        title: i18n.View_AgendaItems_Details,
        type: "Split",
        icon: <i className="ms-Icon ms-Icon--ColumnRightTwoThirds" />,
      },
      {
        name: "Protocol",
        title: i18n.View_AgendaItems_Protocol,
        type: "Split",
        icon: <i className="ms-Icon ms-Icon--ColumnRightTwoThirds" />,
      },
    ],
    [i18n]
  );

  const getView = useCallback(
    (name: string | undefined): View => {
      let view = views.find((x) => x.name == name);
      if (!view) {
        name = "Overview";
        view = views.find((x) => x.name == name);
      }

      if (!view) {
        throw new Error(`View "${name}" not found!`);
      }

      return view;
    },
    [views]
  );

  const handleSetSelectedViewName = useCallback(
    (name: string) => {
      setSelectedViewName(name);
      setCookie(CookieKey, name);
    },
    [setCookie]
  );

  const selectedView = useMemo(() => getView(selectedViewName), [getView, selectedViewName]);

  const value = useMemo<AgendaItemViewsContextProps>(
    () => ({ views, selectedView, setSelectedViewName: handleSetSelectedViewName }),
    [views, selectedView, handleSetSelectedViewName]
  );

  return <AgendaItemViewsContext.Provider value={value}>{children}</AgendaItemViewsContext.Provider>;
};
